import { useState, useEffect } from 'react';
import { Loader2, Clock, Mic, FileText, Globe, Stethoscope, Euro, ChevronDown, ChevronRight, Phone } from 'lucide-react';
import { api } from '@/lib/api';

interface UsageLog {
  id: string;
  usageType: string;
  seconds: number;
  createdAt: string;
  model: string | null;
  sellingCost: number;
}

const USAGE_TYPE_LABELS: Record<string, { label: string; icon: typeof Mic }> = {
  speech_recording: { label: 'Spraakopname', icon: Mic },
  soep_transcription: { label: 'SOEP-transcriptie', icon: FileText },
  translation: { label: 'Vertaling', icon: Globe },
  icpc_suggestion: { label: 'ICPC-suggestie', icon: Stethoscope },
  patient_summary: { label: 'Patiëntsamenvatting (PDF)', icon: FileText },
  acs_call: { label: 'Telefoongesprek (ACS)', icon: Phone },
};

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.round(totalSeconds % 60);
  if (hours > 0) return `${hours}u ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function formatCost(cost: number): string {
  return `€${cost.toFixed(4)}`;
}

const MONTH_NAMES = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

function formatMonth(m: string) {
  const [y, mo] = m.split('-');
  return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${y}`;
}

/**
 * Het kostenoverzicht van één praktijkgebruiker.
 *
 * Dit component staat in twee contexten, en dat maakt uit voor de route:
 *
 *   'eigen'  -- een arts in zijn eigen instellingen. GET /api/me/usage, en die geeft
 *               alleen de regels van de sessie zelf.
 *   'beheer' -- een beheerder die naar een gebruiker kijkt.
 *               GET /api/admin/usage/entries met een filter.
 *
 * In de bron ging beide gevallen naar get-usage met een practice_user_id in de body
 * en een optioneel token. Die functie controleerde alleen iets áls er een token
 * meekwam: zonder token was het verbruik van elke willekeurige gebruiker op te
 * vragen. Vandaar dat de context hier expliciet is en geen token meer bestaat.
 */
export function UserCostsTab({
  practiceUserId,
  bron = 'beheer',
}: {
  practiceUserId: string;
  bron?: 'eigen' | 'beheer';
}) {
  const [logs, setLogs] = useState<UsageLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);

  useEffect(() => {
    if (!practiceUserId) return;
    setIsLoading(true);

    void (async () => {
      try {
        const { entries } = await (bron === 'eigen'
          ? api.get<{ entries: UsageLog[] }>('/me/usage', { query: { limit: 500 } })
          : api.get<{ entries: UsageLog[] }>('/admin/usage/entries', {
              query: { practiceUserId, limit: 500 },
            }));
        setLogs(entries);
      } catch {
        setLogs([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [practiceUserId, bron]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalSeconds = logs.reduce((sum, l) => sum + Number(l.seconds), 0);
  const totalCost = logs.reduce((sum, l) => sum + (l.sellingCost ?? 0), 0);
  const totalActions = logs.length;

  // Group logs by month
  const monthly: Record<string, { count: number; seconds: number; cost: number; logs: UsageLog[] }> = {};
  for (const log of logs) {
    const month = log.createdAt.slice(0, 7);
    if (!monthly[month]) monthly[month] = { count: 0, seconds: 0, cost: 0, logs: [] };
    monthly[month].count += 1;
    monthly[month].seconds += Number(log.seconds);
    monthly[month].cost += log.sellingCost ?? 0;
    monthly[month].logs.push(log);
  }
  const sortedMonths = Object.keys(monthly).sort().reverse().slice(0, 12);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Totaal gebruik</span>
          </div>
          <p className="text-2xl font-bold">{formatDuration(totalSeconds)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {totalActions} AI-acties totaal
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Euro className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Totale kosten</span>
          </div>
          <p className="text-2xl font-bold">€{totalCost.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">verkoopprijs</p>
        </div>
      </div>

      {/* Monthly summary with expandable details */}
      {sortedMonths.length > 0 && (
        <div>
          <span className="text-sm font-medium">Kosten per maand</span>
          <div className="mt-2 rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium w-8"></th>
                  <th className="text-left px-3 py-2 font-medium">Maand</th>
                  <th className="text-right px-3 py-2 font-medium">Acties</th>
                  <th className="text-right px-3 py-2 font-medium">Tijd</th>
                  <th className="text-right px-3 py-2 font-medium">Kosten</th>
                </tr>
              </thead>
              <tbody>
                {sortedMonths.map((m) => {
                  const d = monthly[m];
                  const isExpanded = expandedMonth === m;
                  return (
                    <>
                      <tr
                        key={m}
                        className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                        onClick={() => setExpandedMonth(isExpanded ? null : m)}
                      >
                        <td className="px-3 py-1.5">
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                        </td>
                        <td className="px-3 py-1.5 font-medium">{formatMonth(m)}</td>
                        <td className="text-right px-3 py-1.5">{d.count}</td>
                        <td className="text-right px-3 py-1.5">{formatDuration(d.seconds)}</td>
                        <td className="text-right px-3 py-1.5">€{d.cost.toFixed(2)}</td>
                      </tr>
                      {isExpanded && d.logs.map((log) => {
                        const config = USAGE_TYPE_LABELS[log.usageType] || { label: log.usageType, icon: Clock };
                        const Icon = config.icon;
                        const date = new Date(log.createdAt);
                        return (
                          <tr key={log.id} className="border-b last:border-0 bg-muted/20">
                            <td className="px-3 py-1"></td>
                            <td className="px-3 py-1" colSpan={2}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
                                <div>
                                  <span className="text-xs">{config.label}</span>
                                  <p className="text-[10px] text-muted-foreground">
                                    {date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' })}
                                    {' '}
                                    {date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="text-right px-3 py-1 text-xs">
                              {log.usageType === 'speech_recording' || log.usageType === 'acs_call'
                                ? formatDuration(Number(log.seconds))
                                : '1×'}
                            </td>
                            <td className="text-right px-3 py-1 text-xs">
                              {log.sellingCost != null ? formatCost(log.sellingCost) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {logs.length === 0 && (
        <p className="text-sm text-muted-foreground italic">Nog geen gebruik geregistreerd.</p>
      )}
    </div>
  );
}
